/* Extra Swatches is an accompaniment to a real single swatch, never a standalone gift.
 * Theme guard only: direct API/checkout bypass requires Shopify checkout validation.
 */
(() => {
  'use strict';
  if (window.CzExtraSwatches) return;
  const initial = document.getElementById('cz-extra-initial-state');
  if (!initial) return;
  const root = window.Shopify?.routes?.root || '/';
  const rawFetch = window.fetch.bind(window);
  const failure = 'We couldn’t check your swatches. Please try again before checking out.';
  let state = JSON.parse(initial.textContent);
  let healthy = false;
  let extraUncertain = false;
  let pending = 0;
  let queue = Promise.resolve();
  let checkingOut = false;
  let refreshTimer;
  let noticeTimer;
  const regular = () => state.items.some(item => item.normal && item.quantity > 0);
  const hasExtra = () => state.items.some(item => item.extra && item.quantity > 0);
  const canAdd = () => healthy && pending === 0 && regular();

  function notice(message) {
    let node = document.getElementById('cz-extra-notice');
    if (!node) {
      node = document.createElement('div');
      node.id = 'cz-extra-notice';
      node.setAttribute('role', 'status');
      node.setAttribute('aria-live', 'polite');
      document.body.append(node);
    }
    node.textContent = message;
    node.hidden = false;
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(() => { node.hidden = true; }, 8000);
  }

  function paint() {
    document.querySelectorAll('[data-cz-extra-button]').forEach(button => {
      const inCart = button.classList.contains('add-to-cart-test--in-cart');
      const disabled = button.classList.contains('loading') || Boolean(document.querySelector('[data-mini-cart]')?.classList?.contains('mini-cart--cart-flying'));
      // The collection owns its existing 20-item limit and in-flight lock.
      if (disabled) button.disabled = true;
      else if (!button.classList.contains('loading')) {
        const count = Number(document.querySelector('[data-mini-cart-count]')?.textContent.match(/\d+/)?.[0] || 0);
        button.disabled = !inCart && count >= 20;
      }

    });
  }

  let explanationTrigger;
  let chooseDestination;
  let replayingExtra = false;
  function openExplanation(button) {
    const dialog = document.getElementById('cz-extra-explanation');
    if (!dialog || dialog.open) return;
    explanationTrigger = button;
    chooseDestination = null;
    if (!dialog.dataset.bound) {
      dialog.dataset.bound = 'true';
      const closeButton = dialog.querySelector('[data-cz-extra-close]');
      const chooseButton = dialog.querySelector('[data-cz-extra-choose]');
      closeButton.addEventListener('click', () => dialog.close());
      chooseButton.addEventListener('click', () => {
        chooseDestination = Array.from(document.querySelectorAll('.collection-listing-emily .add-to-cart-test')).find(node => node.getClientRects().length && !node.disabled);
        if (!chooseDestination) {
          location.assign(`${root}collections/free-swatches#cz-normal-swatches`);
          return;
        }
        dialog.close();
      });
      dialog.addEventListener('close', () => {
        const destination = chooseDestination || explanationTrigger;
        destination?.focus({ preventScroll: true });
        if (chooseDestination) destination.scrollIntoView({ block: 'center', behavior: 'auto' });
        chooseDestination = null;
      });
      // Native modal makes the background inert; explicitly cycle its two controls.
      dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        event.preventDefault();
        (document.activeElement === chooseButton ? closeButton : chooseButton).focus();
      });
    }
    dialog.showModal();
    dialog.querySelector('[data-cz-extra-choose]').focus();
  }

  // A native button click includes Enter/Space. Stop it before the collection's
  // optimistic add handler: explaining the rule must never submit a cart write.
  window.addEventListener('click', event => {
    const button = event.target.closest?.('[data-cz-extra-button]');
    if (!button || replayingExtra || button.classList.contains('add-to-cart-test--in-cart')) return;
    if (button.disabled) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    if (healthy && regular()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (healthy) { openExplanation(button); return; }
    serial(readState).then(async () => {
      await queue;
      if (!regular()) { openExplanation(button); return; }
      replayingExtra = true;
      try { button.click(); } finally { replayingExtra = false; }
    }).catch(() => { healthy = false; notice(failure); });
  }, true);

  function serial(work) {
    pending++;
    paint();
    const run = () => navigator.locks
      ? navigator.locks.request('cz-extra-swatches-cart', work)
      : work();
    const task = queue.then(run);
    queue = task.catch(() => {}).finally(() => { pending--; paint(); });
    return task;
  }

  async function readState() {
    const response = await rawFetch(`${root}?section_id=cz-extra-swatches-state&_=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Cart state unavailable');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const node = html.querySelector('[data-cz-extra-state]');
    const value = node && JSON.parse(node.textContent);
    if (!value || !Array.isArray(value.items) || !Array.isArray(value.extraVariants) ||
        value.items.some(item => typeof item.key !== 'string' || typeof item.normal !== 'boolean' || typeof item.extra !== 'boolean' || !Number.isInteger(item.quantity))) {
      throw new Error('Invalid cart state');
    }
    state = value;
    healthy = true;
    extraUncertain = false;
    return value;
  }

  function publishConfirmedCart() {
    document.dispatchEvent(new CustomEvent('cz:swatches:confirmed', { detail: { items: state.items } }));
  }

  async function reconcile() {
    try {
      await readState();
      if (regular() || !hasExtra()) { publishConfirmedCart(); return false; }
      // Only Extra line keys are removed. Never clear the cart or touch other lines.
      const updates = Object.fromEntries(state.items.filter(item => item.extra).map(item => [item.key, 0]));
      const response = await rawFetch(`${root}cart/update.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error('Extra removal failed');
      await readState();
      if (!regular() && hasExtra()) throw new Error('Extra removal not confirmed');
      publishConfirmedCart();
      notice('Extra Swatches removed because no selected swatches remain.');
      return true;
    } catch (error) {
      healthy = false;
      throw error;
    }
  }

  function changed() {
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, detail: { source: 'cz-extra-swatches' } }));
  }

  function refresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      serial(reconcile).then(removed => { if (removed) changed(); }).catch(() => { if (hasExtra()) notice(failure); });
    }, 50);
  }

  // Cart components keep their own request lifecycle.  This guard stays local to
  // the Extra Swatches controls and reconciles after the theme's documented cart
  // events; it deliberately never replaces the page-wide fetch implementation.

  // Capture before the collection's direct-checkout handler and cart form submit.
  // Retry clicks are coalesced; a failed read never authorizes checkout or deletion.
  async function checkout(action) {
    if (checkingOut) return;
    checkingOut = true;
    try {
      // Once pending writes finish, a known cart without Extra keeps its normal
      // checkout path even if this optional feature's state endpoint is offline.
      await queue;
      if (!hasExtra() && !extraUncertain) { action(); return; }
      const removed = await serial(reconcile);
      if (removed) { changed(); return; }
      action();
    } catch (_) { notice(failure); }
    finally { checkingOut = false; }
  }
  let replaying = false;
  window.addEventListener('click', event => {
    if (replaying || (!hasExtra() && !extraUncertain && pending === 0)) return;
    const target = event.target.closest?.('.mini-cart__cart-btn, [name="checkout"], a[href*="/checkout"]');
    // Wallet controls may live in cross-origin frames and cannot be replayed.
    // Leave their normal interaction intact; backend validation owns that boundary.
    if (!target || target.closest('.additional-checkout-buttons, shopify-accelerated-checkout-cart')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout(() => {
      replaying = true;
      try { target.click(); } finally { replaying = false; }
    });
  }, true);
  window.addEventListener('submit', event => {
    if (replaying || (!hasExtra() && !extraUncertain && pending === 0)) return;
    const form = event.target;
    if (!form.matches('form[action*="/cart"]') || (event.submitter && event.submitter.name !== 'checkout')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout(() => {
      replaying = true;
      try { form.requestSubmit(event.submitter || undefined); } finally { replaying = false; }
    });
  }, true);

  window.CzExtraSwatches = { canAdd, refresh };
  document.addEventListener('on:cart:change', event => {
    if (event.detail?.source !== 'cz-extra-swatches') refresh();
  });
  document.addEventListener('on:cart:add', refresh);
  document.addEventListener('theme:open-cart-drawer', refresh);
  document.addEventListener('on:cart:after-merge', paint);
  window.addEventListener('pageshow', refresh);
  window.addEventListener('focus', refresh);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });
  // AJAX collection filtering can replace button markup without cart events.
  new MutationObserver(paint).observe(document.body, { childList: true, subtree: true });
  refresh();
})();
